import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Collapse,
  Chip,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import ServiceSelection from './ServiceSelection';
import StaffSelection from './StaffSelection';
import DateTimeSelection from './DateTimeSelection';
import CustomerInfoForm, { CustomerFormData } from './CustomerInfoForm';
import BookingSummary from './BookingSummary';

import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { Service, TimeSlot, TeamMember } from '../../types';
import { appointmentsApi, teamApi } from '../../firebase/services';
import { findNextAvailableDate } from '../../utils/dateUtils';

const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { services, appointments, blockedTimes, categories, salonSettings, loading, refreshAppointments } = useApp();
  const { currentUser, userData } = useAuth();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerFormData | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [earlierOptions, setEarlierOptions] = useState<{ member: TeamMember; date: string }[]>([]);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const initialService: Service | null = (location.state as any)?.service || null;
  const appliedInitial = React.useRef(false);

  const activeMembers = teamMembers.filter(m => m.isActive);
  const hasMultipleStaff = activeMembers.length > 1;

  const steps = hasMultipleStaff
    ? [
        t('booking.steps.selectService'),
        t('booking.steps.selectStaff'),
        t('booking.steps.dateTime'),
        t('booking.steps.yourInfo'),
        t('booking.steps.confirmation'),
      ]
    : [
        t('booking.steps.selectService'),
        t('booking.steps.dateTime'),
        t('booking.steps.yourInfo'),
        t('booking.steps.confirmation'),
      ];

  // Step index helpers
  const STEP_STAFF = hasMultipleStaff ? 1 : -1;
  const STEP_DATETIME = hasMultipleStaff ? 2 : 1;
  const STEP_INFO = hasMultipleStaff ? 3 : 2;
  const STEP_CONFIRM = hasMultipleStaff ? 4 : 3;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    teamApi.getAll()
      .then(members => setTeamMembers(members))
      .catch(() => {})
      .finally(() => setLoadingTeam(false));
  }, []);

  // Auto-select the only member when there's exactly one
  // Also apply pre-selected service from navigation state
  useEffect(() => {
    if (loadingTeam) return;
    if (activeMembers.length === 1) {
      setSelectedMember(activeMembers[0]);
    }
    if (initialService && !appliedInitial.current) {
      appliedInitial.current = true;
      setSelectedService(initialService);
      // Step 1 = Staff selection (multi-staff) or Date/Time (single staff) — always skip service step
      setActiveStep(1);
    }
  }, [loadingTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser && userData && activeStep === STEP_INFO && !customerInfo) {
      setCustomerInfo({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        notes: '',
        agreeToTerms: false,
        emailNotifications: true,
        smsNotifications: false,
      });
    }
  }, [currentUser, userData, activeStep, customerInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  useEffect(() => {
    if (activeStep === STEP_DATETIME || activeStep === STEP_INFO) {
      refreshAppointments();
    }
  }, [activeStep, refreshAppointments]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the selected date changes, check if other members have earlier availability
  useEffect(() => {
    setEarlierOptions([]);
    setDismissedSuggestion(false);
    if (!selectedDate || !selectedMember || !selectedService || !salonSettings) return;
    const otherMembers = activeMembers.filter(m => m.id !== selectedMember.id);
    if (otherMembers.length === 0) return;

    const options: { member: TeamMember; date: string }[] = [];
    for (const member of otherMembers) {
      const earlierDate = findNextAvailableDate(
        member, selectedService, appointments, blockedTimes, salonSettings, selectedDate
      );
      if (earlierDate) options.push({ member, date: earlierDate });
    }
    // Sort by earliest date first
    options.sort((a, b) => a.date.localeCompare(b.date));
    setEarlierOptions(options);
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    handleNext();
  };

  const handleStaffSelect = (member: TeamMember) => {
    setSelectedMember(member);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setEarlierOptions([]);
    setDismissedSuggestion(false);
    handleNext();
  };

  const handleDateTimeSelect = () => {
    if (selectedDate && selectedTimeSlot) {
      handleNext();
    } else {
      setSnackbarMessage(t('booking.selectDateAndTime'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCustomerInfoSubmit = async (data: CustomerFormData) => {
    setCustomerInfo(data);

    if (!selectedService || !selectedDate || !selectedTimeSlot) {
      setBookingError(t('booking.missingInfo'));
      return;
    }


    setIsSubmitting(true);
    setBookingError(null);

    try {
      await appointmentsApi.create({
        clientId: currentUser?.uid || 'guest',
        clientName: data.firstName && data.lastName
          ? `${data.firstName} ${data.lastName}`
          : data.firstName || data.lastName || 'Client',
        clientPhone: data.phone,
        clientEmail: data.email,
        serviceId: selectedService.id,
        teamMemberId: selectedMember?.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime,
        notes: data.notes,
        status: 'confirmed',
        emailNotifications: data.emailNotifications,
        smsNotifications: data.smsNotifications,
      });

      setBookingSuccess(true);
      handleNext();
      refreshAppointments();
    } catch (error) {
      setBookingError(t('booking.failedToBook'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setActiveStep(0);
    setSelectedService(null);
    setSelectedMember(activeMembers.length === 1 ? activeMembers[0] : null);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setCustomerInfo(null);
    setBookingError(null);
    setBookingSuccess(false);
  };

  const handleSwitchToEarlier = (member: TeamMember) => {
    setSelectedMember(member);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setEarlierOptions([]);
    setDismissedSuggestion(false);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const renderStepContent = (step: number) => {
    if (step === 0) {
      return (
        <ServiceSelection
          services={services}
          categories={categories}
          selectedService={selectedService}
          onSelectService={handleServiceSelect}
        />
      );
    }

    if (step === STEP_STAFF) {
      return (
        <StaffSelection
          teamMembers={activeMembers}
          selectedMember={selectedMember}
          onSelect={handleStaffSelect}
        />
      );
    }

    if (step === STEP_DATETIME) {
      return (
        <Box>
          <DateTimeSelection
            service={selectedService!}
            appointments={appointments}
            blockedTimes={blockedTimes}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            onSelectDate={setSelectedDate}
            onSelectTimeSlot={setSelectedTimeSlot}
            salonSettings={salonSettings}
            teamMember={selectedMember}
            loading={loading.appointments || loading.blockedTimes}
          />

          {/* Earlier availability suggestion */}
          <Collapse in={earlierOptions.length > 0 && !dismissedSuggestion && !!selectedDate}>
            <Paper
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.light',
                bgcolor: 'info.50',
                position: 'relative',
              }}
            >
              <Button
                size="small"
                sx={{ position: 'absolute', top: 6, right: 6, minWidth: 0, p: 0.5 }}
                onClick={() => setDismissedSuggestion(true)}
              >
                <CloseIcon fontSize="small" />
              </Button>

              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, pr: 3 }}>
                💡 D'autres coiffeurs sont disponibles plus tôt :
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {earlierOptions.map(({ member, date }) => (
                  <Box
                    key={member.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={member.imageUrl || ''}
                        sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}
                      >
                        {!member.imageUrl && member.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{member.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Dispo dès le {format(parseISO(date), 'd MMMM', { locale: fr })}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label="Choisir"
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={() => handleSwitchToEarlier(member)}
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Collapse>
        </Box>
      );
    }

    if (step === STEP_INFO) {
      return (
        <>
          {bookingError && (
            <Alert severity="error" sx={{ mb: 2 }}>{bookingError}</Alert>
          )}
          <CustomerInfoForm
            onSubmit={handleCustomerInfoSubmit}
            defaultValues={customerInfo || undefined}
            isSubmitting={isSubmitting}
          />
        </>
      );
    }

    if (step === STEP_CONFIRM) {
      return (
        <>
          {bookingSuccess && customerInfo && selectedService && selectedDate && selectedTimeSlot && (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('booking.successAlert')}
              </Alert>
              <BookingSummary
                service={selectedService}
                date={selectedDate}
                timeSlot={selectedTimeSlot}
                customerInfo={customerInfo}
                teamMember={selectedMember}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={handleStartOver}>
                  {t('booking.bookAnother')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  color="primary"
                  sx={{ color: 'text.primary' }}
                >
                  {t('booking.returnHome')}
                </Button>
              </Box>
            </>
          )}
          {!bookingSuccess && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" color="error" gutterBottom>
                {t('booking.failedTitle')}
              </Typography>
              <Typography variant="body1" paragraph>
                {bookingError || t('booking.failedDesc')}
              </Typography>
              <Button
                variant="contained"
                onClick={handleStartOver}
                color="primary"
                sx={{ color: 'text.primary' }}
              >
                {t('booking.tryAgain')}
              </Button>
            </Paper>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" align="center" gutterBottom>
        {t('booking.title')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {(loading.services || loadingTeam) && activeStep === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {activeStep > 0 && activeStep < STEP_CONFIRM && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack}>
                {t('booking.back')}
              </Button>
              {activeStep === STEP_DATETIME && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDateTimeSelect}
                  disabled={!selectedDate || !selectedTimeSlot}
                  sx={{ color: 'text.primary' }}
                >
                  {t('booking.next')}
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <Paper
            elevation={3}
            sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
          >
            <CircularProgress />
            <Typography>{t('booking.processing')}</Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default BookingPage;
